import React, { useEffect, useState } from 'react';
import { useLanguage } from './context/Languagecontext';



const RotatePhonePopup = ({ excludeWhen = [] }) => {
    const { t } = useLanguage();
    const [showRotatePopup, setShowRotatePopup] = useState(false);

    useEffect(() => {
        // Check if popup should be excluded based on parent conditions
        if (excludeWhen.some(condition => condition)) {
            setShowRotatePopup(false);
            return;
        }

        let isKeyboardOpen = false;
        let initialHeight = window.innerHeight;

        const checkOrientation = () => {
            const currentHeight = window.innerHeight;
            const isLandscape = window.innerWidth > window.innerHeight;
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const isSmallScreen = window.innerWidth < 768;

            // Detect keyboard: if height decreased significantly, keyboard is likely open
            const heightDifference = initialHeight - currentHeight;
            isKeyboardOpen = heightDifference > 150;

            // Only show rotate popup if:
            // 1. Device is in landscape
            // 2. Device is mobile or small screen
            // 3. Keyboard is NOT open
            // 4. No excluded conditions are true
            const shouldShow = isLandscape &&
                (isMobile || isSmallScreen) &&
                !isKeyboardOpen &&
                !excludeWhen.some(condition => condition);

            setShowRotatePopup(shouldShow);
        };

        // Check immediately on mount
        checkOrientation();
        initialHeight = window.innerHeight;

        // Add a small delay for initial check
        setTimeout(checkOrientation, 100);

        // Check on window resize
        const handleResize = () => {
            // Update initial height when not in landscape to get accurate baseline
            if (window.innerWidth <= window.innerHeight) {
                initialHeight = window.innerHeight;
            }
            checkOrientation();
        };

        // Handle orientation change with delay
        const handleOrientationChange = () => {
            setTimeout(() => {
                initialHeight = window.innerHeight;
                checkOrientation();
            }, 200);
        };

        // Handle focus events (keyboard opening)
        const handleFocusIn = () => {
            setTimeout(() => {
                isKeyboardOpen = true;
                checkOrientation();
            }, 300);
        };

        // Handle blur events (keyboard closing)
        const handleFocusOut = () => {
            setTimeout(() => {
                isKeyboardOpen = false;
                initialHeight = window.innerHeight;
                checkOrientation();
            }, 300);
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleOrientationChange);
        window.addEventListener('focusin', handleFocusIn);
        window.addEventListener('focusout', handleFocusOut);

        // Screen orientation API
        if (window.screen?.orientation) {
            window.screen.orientation.addEventListener('change', handleOrientationChange);
        }

        // Visual viewport API for better keyboard detection (iOS Safari)
        const handleViewportResize = () => {
            if (window.visualViewport) {
                const viewportHeight = window.visualViewport.height;
                const windowHeight = window.innerHeight;
                isKeyboardOpen = viewportHeight < windowHeight - 100;
                checkOrientation();
            }
        };

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleViewportResize);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.removeEventListener('focusin', handleFocusIn);
            window.removeEventListener('focusout', handleFocusOut);

            if (window.screen?.orientation) {
                window.screen.orientation.removeEventListener('change', handleOrientationChange);
            }

            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleViewportResize);
            }
        };
    }, [excludeWhen]);

    if (!showRotatePopup) return null;

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
                zIndex: 999999,
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                backdropFilter: 'blur(10px)'
            }}
        >
            <div
                className="text-center text-white p-4"
                style={{
                    maxWidth: '90vw',
                }}
            >
                {/* Rotate Phone Icon */}
                <div
                    className="mb-4"
                    style={{
                        animation: 'rotate 2s ease-in-out infinite'
                    }}
                >
                    <svg
                        width="100"
                        height="100"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                            filter: 'drop-shadow(0 4px 6px rgba(255, 255, 255, 0.1))'
                        }}
                    >
                        <path
                            d="M7 2H17C18.1046 2 19 2.89543 19 4V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V4C5 2.89543 5.89543 2 7 2Z"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M12 18H12.01"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M9 5H15"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>

                {/* Message */}
                <h2
                    className="GTBold mb-3"
                    style={{
                        fontSize: 'clamp(20px, 5vw, 28px)',
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                >
                    {t?.rotate?.title}
                </h2>
                <p
                    className="GTRegular"
                    style={{
                        fontSize: 'clamp(14px, 3vw, 18px)',
                        color: '#cccccc',
                        maxWidth: '300px',
                        margin: '0 auto'
                    }}
                >
                    {t?.rotate?.description}
                </p>

                {/* Decorative arrows */}
                <div
                    className="mt-4"
                    style={{
                        animation: 'bounce 1s ease-in-out infinite'
                    }}
                >
                    <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M7 10L12 15L17 10"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M7 5L12 10L17 5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            opacity="0.5"
                        />
                    </svg>
                </div>
            </div>

            <style>
                {`
                    @keyframes rotate {
                        0%, 100% {
                            transform: rotate(0deg);
                        }
                        25% {
                            transform: rotate(-90deg);
                        }
                        50% {
                            transform: rotate(-90deg);
                        }
                        75% {
                            transform: rotate(0deg);
                        }
                    }


                    @keyframes bounce {
                        0%, 100% {
                            transform: translateY(0);
                        }
                        50% {
                            transform: translateY(-10px);
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default RotatePhonePopup;